/*
 * Copyright (c) 2019 Frontier Developments PLC.
 */

import {Component, Vue, Watch} from 'vue-property-decorator';
import {Getter} from 'vuex-class';
import {SpeciesInterface} from '@/store/modules/game/types';
import {TrophyCabinetMode, TrophyGrade} from '@/store/modules/trophy-cabinet/types';
import {SvgNodeStorageInterface} from '@/components/SpeciesAchievementListing/types';
import {eventBus} from '@/services/eventbus';
import Popper from 'vue-popperjs';

/**
 * Species Achievement Listing Component.
 * @author Jonny Cook <jcook@frontier.co.uk>
 */
@Component({
    name: 'c-species-achievement-listing',
    components: {
        popper: Popper,
    },
})
export default class SpeciesAchievementListing extends Vue {
    public static SVG_EVENT: string = 'svgLoaded';
    private static DOMESTIC_ANIMALS = ['Alpaca', 'AlpineGoat', 'TamworthPig', 'HillRadnor', 'HighlandCattle', 'StandardDonkey', 'SussexChicken', 'BactrianCamel', 'Llama', 'DromedaryCamel'];

    @Getter('species', {namespace: 'gameModule'})
    public species!: SpeciesInterface[];

    @Getter('trophy', {namespace: 'trophyCabinetModule'})
    public trophy!: (species: SpeciesInterface) => TrophyGrade;

    @Getter('contribution', {namespace: 'trophyCabinetModule'})
    public contribution!: (species: SpeciesInterface) => number;

    @Getter('mode', {namespace: 'trophyCabinetModule'})
    public mode!: TrophyCabinetMode;

    public repository: SvgNodeStorageInterface = {};

    @Watch('mode')
    public modeChanged(): void {
        this.refreshSvgIcon(this.mode);
    }

    /**
     * Created lifecycle event.
     * Initialises listeners for svg events.
     */
    public created(): void {
        eventBus.$on(SpeciesAchievementListing.SVG_EVENT, (args: any) => {
            this.refreshSvgIcon(args.key as TrophyCabinetMode);
        });
    }

    public modeFilteredSpecies(): SpeciesInterface[] {
        if (TrophyCabinetMode.RELEASED !== this.mode) {
            return this.species || []
        }

        return this.species.filter((speciesItem: SpeciesInterface) => {
            return !SpeciesAchievementListing.DOMESTIC_ANIMALS.includes(speciesItem.id);
        })
    }

    /**
     * Get medal count for species.
     * @return {(species: SpeciesInterface, medal: string) => string}
     */
    public get medalCount(): (species: SpeciesInterface, medal: string) => string {
        return (species: SpeciesInterface, medal: string): string => {
            if (!species.medals || !species.medals[this.mode] || !species.medals[this.mode][medal]) {
                return '-';
            }

            return `${species.medals[this.mode][medal]}`;
        };
    }

    /**
     * Get medal count for species.
     * @param {SpeciesInterface} species
     * @param {string} medal
     * @return {string}
     */
    public getMedalCount(species: SpeciesInterface, medal: string): string {
        if (!species.medals || !species.medals[this.mode] || !species.medals[this.mode][medal]) {
            return '-';
        }

        return `${species.medals[this.mode][medal]}`;
    }

    /**
     * Destroy lifecycle event.
     * Tears down listeners.
     */
    public beforeDestroy(): void {
        eventBus.$off(SpeciesAchievementListing.SVG_EVENT);
    }

    /**
     * Mounted lifecycle event.
     * Initialises image sets.
     */
    public mounted(): void {
        this.getSvg(require('@/assets/icons/fertility.svg'), TrophyCabinetMode.BORN);
        this.getSvg(require('@/assets/icons/release_to_wild.svg'), TrophyCabinetMode.RELEASED);
        this.getSvg(require('@/assets/icons/trade_alt.svg'), TrophyCabinetMode.BOUGHT);
        this.getSvg(require('@/assets/icons/sold.svg'), TrophyCabinetMode.SOLD);
    }

    /**
     * Clones the loaded SVGs and adds to document.
     * @param {TrophyCabinetMode} mode
     */
    public async refreshSvgIcon(mode: TrophyCabinetMode): Promise<void> {
        if (mode !== this.mode) {
            return;
        }

        await this.$nextTick();

        document.querySelectorAll('.mode-icon').forEach((parent: Element) => {
            while (parent.firstChild) {
                parent.removeChild(parent.firstChild);
            }

            if (!!this.repository[mode]) {
                parent.appendChild((this.repository[mode] as HTMLElement).cloneNode(true));
            }
        });
    }

    /**
     * Loads the SVG items into memory.
     *
     * @param url
     * @param {string} key
     * @return {Promise<any>}
     */
    public async getSvg(url: any, key: TrophyCabinetMode): Promise<any> {
        try {
            const http: XMLHttpRequest = new XMLHttpRequest();

            http.open('GET', url, true);
            http.setRequestHeader('Content-Type', 'text/xml; charset=UTF-8');
            http.onload = (): void => {
                if (http.readyState === http.DONE && http.status === 200 && null !== http.responseXML) {
                    Vue.set(this.repository, key, http.responseXML.documentElement);
                    eventBus.$emit(SpeciesAchievementListing.SVG_EVENT, {key});
                }
            };
            await http.send(null);
        } catch (e) {
            // do nothing
        }
    }

    /**
     * Build animal image listing.
     * @param {SpeciesInterface} species
     * @param {string} sex
     * @param {string} size
     * @return {string}
     */
    public buildAnimalImage(species: SpeciesInterface, sex: string = 'm', size: string = 'normal'): any {
        try {
            return require(`@/assets/animals/thumbnail_${species.id}_${sex}_${size}.png`);
        } catch (e) {
            return require('@/assets/_animal.jpg');
        }
    }
}
